/** Окно «Референс / Генерация / Доработка»: кто «Генерация», список «Доработок», лайк из pick. Без импорта api: гоняется node:test. */

export type Shot = { variantId: number; path: string; pick: string; label: string; kind: string };

type CardLike = {
  kind?: string;
  variant: { id: number; asset_path: string; pick: string };
  video?: unknown;
  generation?: { inputs: unknown[] };
  edit_of?: { variant_id: number; asset_path: string; pick: string; note: string | null } | null;
  enhance_of?: { variant_id: number; asset_path: string; pick: string } | null;
  edits?: Run[];
  enhances?: Run[];
};

type Run = { generation_id?: number; note?: string | null; variants: number[]; variant_paths: string[]; variant_picks: string[] };

const ENHANCE_LABEL = "улучшено";

/** Вид по умолчанию, пока вид карточки неизвестен. */
const kindOf = (card: CardLike | null | undefined): string => card?.kind ?? "manual";

const editLabel = (id: number, note: string | null | undefined): string =>
  `Доработка · изображение #${id}` + (note ? ` — «${note}»` : "");

/** Готовые доработки и улучшения самой карточки (без учёта её исходника), по порядку запуска (id генерации = дата). */
export function ownEdits(card: CardLike): Shot[] {
  const runs = [
    ...(card.edits ?? []).map((r) => ({ r, enh: false })),
    ...(card.enhances ?? []).map((r) => ({ r, enh: true })),
  ];
  runs.sort((a, b) => (a.r.generation_id ?? 0) - (b.r.generation_id ?? 0));
  const own: Shot[] = [];
  for (const { r, enh } of runs) {
    r.variants.forEach((id, i) => {
      own.push({
        variantId: id,
        path: r.variant_paths[i] ?? "",
        pick: r.variant_picks[i] ?? "none",
        label: enh ? ENHANCE_LABEL : editLabel(id, r.note),
        kind: enh ? "enhance" : kindOf(card),
      });
    });
  }
  return own;
}

/**
 * На доработке «Генерация» — её исходник, «Доработки» — готовые доработки ИСХОДНИКА (с этой карточкой), старт — на ней;
 * пока карточка исходника не пришла (или упала) — одна эта карточка. На оригинале — его собственные доработки.
 */
export function triptych(
  card: CardLike,
  origin?: CardLike | null,
): { generation: Shot; edits: Shot[]; startEdit: number; needsOriginCard: boolean } {
  const o = card.edit_of ?? card.enhance_of;
  if (o) {
    const self: Shot = {
      variantId: card.variant.id,
      path: card.variant.asset_path,
      pick: card.variant.pick,
      label: card.edit_of ? editLabel(card.variant.id, card.edit_of.note) : ENHANCE_LABEL,
      kind: card.edit_of ? kindOf(card) : "enhance",
    };
    const fromOrigin = origin ? ownEdits(origin) : [];
    const at = fromOrigin.findIndex((x) => x.variantId === self.variantId);
    const edits = at >= 0 ? fromOrigin.map((x, i) => (i === at ? { ...x, pick: self.pick } : x)) : [self];
    return {
      generation: { variantId: o.variant_id, path: o.asset_path, pick: o.pick, label: `Генерация · изображение #${o.variant_id}`, kind: kindOf(origin) },
      edits,
      startEdit: at >= 0 ? at : 0,
      needsOriginCard: true,
    };
  }
  return {
    generation: {
      variantId: card.variant.id,
      path: card.variant.asset_path,
      pick: card.variant.pick,
      label: `Генерация · изображение #${card.variant.id}`,
      kind: kindOf(card),
    },
    edits: ownEdits(card),
    startEdit: 0,
    needsOriginCard: false,
  };
}

/** Индекс входа «Как сделано» (у доработки это [anchor, …входы исходника]) в списке колонки входов; якорь/нет — 0. */
export function leftInputIndex(inputs: { asset_id: number; role: string }[], clicked: { asset_id: number; role: string }): number {
  if (clicked.role === "anchor") return 0;
  const i = inputs.findIndex((x) => x.asset_id === clicked.asset_id && x.role === clicked.role);
  return i >= 0 ? i : 0;
}

type InputLike = { asset_id: number; role: string };

/** Колонки входов (№440): «Товар» — фото платья (`product`), «Референс» — остальное (сцена, лицо, вставки), порядок прежний. */
export function splitInputs<T extends InputLike>(inputs: T[]): { product: T[]; refs: T[] } {
  return { product: inputs.filter((x) => x.role === "product"), refs: inputs.filter((x) => x.role !== "product") };
}

/** С какого входа открыть колонки: кликнутый вход — в своей колонке, другая — на первом. */
export function startIndices(cols: { product: InputLike[]; refs: InputLike[] }, clicked: InputLike | undefined): { product: number; refs: number } {
  if (!clicked) return { product: 0, refs: 0 };
  return clicked.role === "product"
    ? { product: leftInputIndex(cols.product, clicked), refs: 0 }
    : { product: 0, refs: leftInputIndex(cols.refs, clicked) };
}

export const likeState = (pick: string): { liked: boolean; disliked: boolean } => ({
  liked: pick === "picked",
  disliked: pick === "rejected",
});

export function canCompare(card: CardLike): boolean {
  if (card.video) return false;
  return (card.generation?.inputs.length ?? 0) > 0 || triptych(card).edits.length > 0 || !!card.edit_of || !!card.enhance_of;
}
